import { Check, Minus } from 'lucide-react';
import type { StockAnalogLensData } from '@/mocks/stockAnalogMock';
import { formatPct, pctChipClass } from '@/components/AnalogLensAccordion';
import {
  barWidthPct,
  buildTapeFingerprint,
  normalizeAnalogTape,
  normalizeReferenceTape,
  type AnalogTapeStats,
} from '@/utils/analogTapeFingerprint';

type Props = {
  referenceTape?: Record<string, unknown> | AnalogTapeStats | null;
  analogTape?: Record<string, unknown> | AnalogTapeStats | null;
  referenceLabel?: string;
  analogLabel?: string;
  accent?: StockAnalogLensData['accent'];
  className?: string;
};

const ACCENT_BAR: Record<StockAnalogLensData['accent'], string> = {
  emerald: 'bg-emerald-500',
  blue: 'bg-blue-500',
  violet: 'bg-violet-500',
};

function formatLevel(value: number, id: string): string {
  if (id === 'y10') return `${value.toFixed(2)}%`;
  return value.toFixed(1);
}

function TapeBar({
  value,
  scale,
  mode,
  tone,
}: {
  value: number;
  scale: number;
  mode: 'pct' | 'level';
  tone: 'reference' | 'analog';
}) {
  const width = barWidthPct(value, scale);
  const barColor = tone === 'reference' ? 'bg-gray-800' : 'bg-gray-400';
  const isPct = mode === 'pct';

  if (isPct) {
    const positive = value >= 0;
    return (
      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gray-300" />
        <div
          className={`absolute top-0 h-full rounded-full ${barColor}`}
          style={{
            width: `${width / 2}%`,
            left: positive ? '50%' : undefined,
            right: positive ? undefined : '50%',
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
      <div
        className={`absolute left-0 top-0 h-full rounded-full ${barColor}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function TapeRow({
  label,
  today,
  analog,
  scale,
  mode,
  id,
  aligned,
  analogBarClass,
}: {
  label: string;
  today: number | null;
  analog: number | null;
  scale: number;
  mode: 'pct' | 'level';
  id: string;
  aligned: boolean;
  analogBarClass: string;
}) {
  if (today == null && analog == null) return null;

  return (
    <div className="grid grid-cols-[2.5rem_1fr] gap-x-2 gap-y-1.5 sm:grid-cols-[2.5rem_1fr_auto] sm:items-center">
      <span className="pt-1 text-[11px] font-semibold text-gray-500 sm:pt-0">{label}</span>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Today
          </span>
          {today != null ? (
            <>
              <TapeBar value={today} scale={scale} mode={mode} tone="reference" />
              <span
                className={`w-12 shrink-0 text-right text-[11px] font-medium tabular-nums ${mode === 'pct' ? pctChipClass(today) : 'text-gray-700'}`}
              >
                {mode === 'pct' ? formatPct(today) : formatLevel(today, id)}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-gray-400">—</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Analog
          </span>
          {analog != null ? (
            <>
              <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                {mode === 'pct' ? (
                  <>
                    <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gray-300" />
                    <div
                      className={`absolute top-0 h-full rounded-full ${analogBarClass}`}
                      style={{
                        width: `${barWidthPct(analog, scale) / 2}%`,
                        left: analog >= 0 ? '50%' : undefined,
                        right: analog >= 0 ? undefined : '50%',
                      }}
                    />
                  </>
                ) : (
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full ${analogBarClass}`}
                    style={{ width: `${barWidthPct(analog, scale)}%` }}
                  />
                )}
              </div>
              <span
                className={`w-12 shrink-0 text-right text-[11px] font-medium tabular-nums ${mode === 'pct' ? pctChipClass(analog) : 'text-gray-700'}`}
              >
                {mode === 'pct' ? formatPct(analog) : formatLevel(analog, id)}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-gray-400">—</span>
          )}
        </div>
      </div>
      <div className="col-span-2 flex justify-end sm:col-span-1 sm:justify-center">
        {today != null && analog != null ? (
          aligned ? (
            <span
              className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
              title="Same direction and close magnitude"
            >
              <Check className="h-3 w-3" />
              Align
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
              title="Direction or magnitude diverged"
            >
              <Minus className="h-3 w-3" />
              Gap
            </span>
          )
        ) : null}
      </div>
    </div>
  );
}

export function AnalogTapeFingerprint({
  referenceTape,
  analogTape,
  referenceLabel = 'Today',
  analogLabel = 'Analog',
  accent = 'emerald',
  className = '',
}: Props) {
  const ref = normalizeReferenceTape(
    referenceTape as Record<string, unknown> | null | undefined,
  );
  const analog = normalizeAnalogTape(analogTape as Record<string, unknown> | null | undefined);

  const rows = buildTapeFingerprint(ref, analog);
  if (rows.length < 2) return null;

  const alignedCount = rows.filter((r) => r.aligned).length;
  const analogBarClass = ACCENT_BAR[accent];

  return (
    <div
      className={`rounded-xl border border-gray-100 bg-white px-3 py-3 ${className}`}
      aria-label="Tape fingerprint comparison"
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Tape fingerprint
          </p>
          <p className="text-xs text-gray-500">
            {referenceLabel} vs {analogLabel} — same scale per row
          </p>
        </div>
        <span className="rounded-lg bg-gray-50 px-2 py-1 text-[11px] font-medium text-gray-700">
          {alignedCount}/{rows.length} dials aligned
        </span>
      </div>
      <div className="space-y-3">
        {rows.map((row) => (
          <TapeRow
            key={row.id}
            id={row.id}
            label={row.label}
            today={row.today}
            analog={row.analog}
            scale={row.scale ?? 1.5}
            mode={row.mode}
            aligned={row.aligned}
            analogBarClass={analogBarClass}
          />
        ))}
      </div>
      <p className="mt-3 text-[10px] leading-relaxed text-gray-400">
        Center line = flat day for returns. Dark bar = {referenceLabel}; colored bar = analog
        session. Align = same sign and similar size.
      </p>
    </div>
  );
}
